import { useState, useEffect } from "react";
import { useUser } from "@supabase/auth-helpers-react";
import { Plus, Trash2, Pencil, HelpCircle, RefreshCw, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { generateDefaultTemplates } from "@/utils/emailTemplateUtils";

const templateSchema = z.object({
  name: z.string().min(1, "Template name is required"),
  content: z.string().min(1, "Template content is required"),
  template_type: z.enum([
    "initial",
    "reminder_1",
    "reminder_2",
    "reminder_4",
  ]),
});

type TemplateFormData = z.infer<typeof templateSchema>;

interface Template {
  id: string;
  name: string;
  content: string;
  template_type: "initial" | "reminder_1" | "reminder_2" | "reminder_4";
}

interface BusinessProfile {
  id?: string;
  user_id?: string;
  default_lang_preference?: string | null;
  // Add other fields from business_profiles as needed
}

const getTemplateColor = (type: Template['template_type']) => {
  switch (type) {
    case 'initial':
      return 'bg-blue-50';
    case 'reminder_1':
      return 'bg-blue-50';
    case 'reminder_2':
      return 'bg-blue-50';
    case 'reminder_4':
      return 'bg-blue-50';
    default:
      return 'bg-blue-50';
  }
};

const getTemplateTypeLabel = (type: Template['template_type']) => {
  switch (type) {
    case 'initial':
      return 'Initial Email';
    case 'reminder_1':
      return 'First Reminder';
    case 'reminder_2':
      return 'Second Reminder';
    case 'reminder_4':
      return 'Final Reminder';
    default:
      return type;
  }
};

const DEFAULT_ENGLISH_TEMPLATE_NAMES = [
  'Default Initial Email',
  'Default First Reminder',
  'Default Second Reminder',
  'Default Final Reminder'
];

const QUILL_TOOLBAR_OPTIONS = [
  ['bold', 'italic', 'underline'],
  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
  [{ 'color': [] }],
  ['clean'],
];

const ensureInlineStyles = (content: string): string => {
  if (content.includes('style="font-size:') || content.includes('style="font-weight:') || content.includes('style="color:')) {
    return content;
  }

  // First replace paragraph styles
  let processedContent = content.replace(
    /<p>/g,
    '<p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">'
  );

  // Check if content is already wrapped in a div
  if (!processedContent.startsWith('<div')) {
    // Wrap the entire content in a styled div
    processedContent = `<div style="font-family: Arial, sans-serif; padding: 20px; border-radius: 8px; background-color: #e6f3ff;">${processedContent}</div>`;
  } else {
    // Replace existing divs with styled divs
    processedContent = processedContent.replace(
      /<div>/g,
      '<div style="font-family: Arial, sans-serif; padding: 20px; border-radius: 8px; background-color: #e6f3ff;">'
    );
  }

  return processedContent;
};

export function EmailTemplates() {
  const user = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [showLanguageUpdateDialog, setShowLanguageUpdateDialog] = useState(false);
  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);
  const { t, i18n } = useTranslation();

  const form = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: "",
      content: "",
      template_type: "initial",
    },
  });

  useEffect(() => {
    if (user) {
      fetchTemplates();
    }
  }, [user]);

  useEffect(() => {
    if (editingTemplate) {
      form.reset({
        name: editingTemplate.name,
        content: editingTemplate.content,
        template_type: editingTemplate.template_type,
      });
    } else {
      form.reset({
        name: "",
        content: "",
        template_type: "initial",
      });
    }
  }, [editingTemplate, form]);

  const fetchTemplates = async () => {
    try {
      if (!user) return;

      // First, check if the user has already set a language preference
      const { data: businessProfile, error: profileError } = await supabase
        .from("business_profiles")
        .select("default_lang_preference")
        .eq("user_id", user.id)
        .single();

      if (profileError && !profileError.message.includes('No rows found')) {
        throw profileError;
      }

      const { data, error } = await supabase
        .from("email_templates")
        .select("*")
        .eq("user_id", user.id);

      if (error) throw error;

      const validTemplates = data.filter(
        (template) => template.template_type !== "reminder_3"
      ) as Template[];

      const currentLanguage = i18n.language;

      const defaultTemplates = generateDefaultTemplates(t);

      const templateTypes = ["initial", "reminder_1", "reminder_2", "reminder_4"] as const;
      const existingTypes = validTemplates.map(t => t.template_type);

      const createPromises = [];

      for (const type of templateTypes) {
        if (!existingTypes.includes(type)) {
          const template = defaultTemplates[type];

          const newTemplate = {
            user_id: user.id,
            name: template.name,
            content: template.content,
            template_type: template.template_type,
          };

          createPromises.push(
            supabase.from("email_templates").insert(newTemplate)
          );

          validTemplates.push({
            ...newTemplate,
            id: crypto.randomUUID(),
          });
        }
      }

      if (createPromises.length > 0) {
        await Promise.all(createPromises);
      }

      const sortedTemplates = validTemplates.sort((a, b) => {
        const templateOrder = {
          'initial': 1,
          'reminder_1': 2,
          'reminder_2': 3,
          'reminder_4': 4
        };

        return templateOrder[a.template_type] - templateOrder[b.template_type];
      });

      setTemplates(sortedTemplates);

      // Check if we should show the language update dialog
      const hasSetLanguagePreference = (businessProfile as BusinessProfile)?.default_lang_preference !== null;

      if (!hasSetLanguagePreference && !currentLanguage.startsWith('en')) {
        const hasDefaultEnglishTemplates = sortedTemplates.some(template =>
          DEFAULT_ENGLISH_TEMPLATE_NAMES.includes(template.name)
        );

        if (hasDefaultEnglishTemplates) {
          setShowLanguageUpdateDialog(true);
        }
      }
    } catch (error: any) {
      toast.error(t("toasts.template_load_failed", "Failed to load template: "), {
        description: error.message,
      });
    }
  };

  const saveLanguagePreference = async (preference: string) => {
    if (!user) return;

    try {
      // Update the business_profiles table with the language preference
      const { error } = await supabase
        .from("business_profiles")
        .update({
          default_lang_preference: preference
        } as any)
        .eq("user_id", user.id);

      if (error) throw error;
    } catch (error: any) {
      console.error("Failed to save language preference:", error);
    }
  };

  const updateTemplatesLanguage = async () => {
    try {
      if (!user) return;
      setIsUpdatingLanguage(true);

      const currentLanguage = i18n.language;
      const defaultTemplates = generateDefaultTemplates(t);

      const templatesToUpdate = templates.filter(template =>
        DEFAULT_ENGLISH_TEMPLATE_NAMES.includes(template.name)
      );

      const updatePromises = templatesToUpdate.map(template => {
        const newTemplate = defaultTemplates[template.template_type];

        return supabase
          .from("email_templates")
          .update({
            name: newTemplate.name,
            content: newTemplate.content,
          })
          .eq("id", template.id);
      });

      await Promise.all(updatePromises);

      // Save user preference in business_profiles table
      await saveLanguagePreference(currentLanguage);

      toast.success(t("toasts.templates_language_updated", "Templates updated to {{language}}", { language: i18n.language === 'pt' ? 'Portuguese' : i18n.language === 'fr' ? 'French' : i18n.language }));
      setShowLanguageUpdateDialog(false);

      fetchTemplates();
    } catch (error: any) {
      toast.error(t("toasts.templates_language_update_failed", "Failed to update templates language: "), {
        description: error.message,
      });
    } finally {
      setIsUpdatingLanguage(false);
    }
  };

  const onSubmit = async (formData: TemplateFormData) => {
    try {
      setIsLoading(true);
      if (!user) return;

      const processedContent = ensureInlineStyles(formData.content);

      if (editingTemplate) {
        const { error: updateError } = await supabase
          .from("email_templates")
          .update({
            name: formData.name,
            content: processedContent,
            template_type: formData.template_type,
          })
          .eq("id", editingTemplate.id);

        if (updateError) throw updateError;
        toast.success(t("toasts.template_updated_success", "Template updated successfully"));
      } else {
        const { data: existingTemplate, error: checkError } = await supabase
          .from("email_templates")
          .select("*")
          .eq("user_id", user.id)
          .eq("name", formData.name)
          .maybeSingle();

        if (checkError) throw checkError;

        if (existingTemplate) {
          toast.error(t("toasts.template_name_exists", "A template with this name already exists"));
          return;
        }

        const templateData = {
          user_id: user.id,
          name: formData.name,
          content: processedContent,
          template_type: formData.template_type,
        };

        const { error: insertError } = await supabase
          .from("email_templates")
          .insert(templateData);

        if (insertError) throw insertError;
        toast.success(t("toasts.template_created_success", "Template created successfully"));
      }

      setIsDialogOpen(false);
      setEditingTemplate(null);
      form.reset();
      fetchTemplates();
    } catch (error: any) {
      toast.error(editingTemplate ? t("toasts.template_updated_failed", "Failed to update template: ") : t("toasts.template_created_failed", "Failed to create template: "), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from("email_templates")
        .delete()
        .eq("id", id);

      if (error) {
        if (error.message && error.message.includes("violates foreign key constraint")) {
          throw new Error(t("toasts.template_in_use",
            "This template cannot be deleted because it is being used by one or more scheduled invoices. Please update your invoice schedules first."));
        }
        throw error;
      }

      toast.success(t("toasts.template_delete_success", "Template deleted successfully"));
      fetchTemplates();
    } catch (error: any) {
      toast.error(t("toasts.template_delete_failed", "Failed to delete template: "), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-8 w-full">
      <div className="space-y-4 w-full">
        <Accordion type="multiple" className="space-y-2 w-full">
          {templates.map((template) => (
            <AccordionItem
              key={template.id}
              value={template.id}
              className={cn(
                "border rounded-lg overflow-hidden bg-white text-[#34375B] w-full",
                getTemplateColor(template.template_type)
              )}
            >
              <AccordionTrigger className="px-2 sm:px-4 py-2 hover:no-underline bg-white w-full">
                <div className="grid grid-cols-[1fr,auto] sm:grid-cols-[minmax(0,0.5fr),minmax(0,1fr),auto] gap-2 sm:gap-4 items-center w-full">
                  <h3 className="font-sm text-[#5E64A7] truncate text-left">
                    {template.name}
                  </h3>
                  <h2 className="font-sm text-[#34375B] text-left hidden sm:block">
                    {getTemplateTypeLabel(template.template_type)}
                  </h2>
                  <div className="flex items-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        handleEdit(template);
                      }}
                      className="text-[#636782] hover:text-[#34375B] hover:bg-transparent"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive text-[#F39D96] hover:text-[#F44336] hover:bg-transparent pr-2 sm:pr-4"
                      onClick={(e) => {
                        e.preventDefault();
                        deleteTemplate(template.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-2 sm:px-4 pb-4 bg-[#F1F6F5] w-full">
                <div
                  className="max-w-none text-left w-full"
                  dangerouslySetInnerHTML={{ __html: template.content }}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <Dialog open={showLanguageUpdateDialog} onOpenChange={setShowLanguageUpdateDialog}>
        <DialogContent className="bg-[#F1F6F5] border-0">
          <DialogHeader>
            <DialogTitle className="text-[#34375B]">
              {t("templates.update_language_title", "Update Template Language")}
            </DialogTitle>
            <DialogDescription className="text-[#34375B]/80 font-thin">
              {t("templates.update_language_description", "Your email templates are currently in English, but your app language is {{language}}. Would you like to update your templates to {{language}}?",
                { language: i18n.language === 'pt' ? 'Portuguese' : i18n.language === 'fr' ? 'French' : i18n.language }
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                // Save user preference when they choose to keep English
                saveLanguagePreference('en');
                setShowLanguageUpdateDialog(false);
              }}
              className="border border-gray-300 hover:border-0 hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200"
            >
              {t("templates.keep_english", "Keep English")}
            </Button>
            <Button
              onClick={updateTemplatesLanguage}
              disabled={isUpdatingLanguage}
              className="bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200"
            >
              {isUpdatingLanguage ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("templates.updating", "Updating...")}
                </>
              ) : (
                t("templates.update_to_current_language", "Update to {{language}}",
                  { language: i18n.language === 'pt' ? 'Portuguese' : i18n.language === 'fr' ? 'French' : i18n.language }
                )
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex justify-end w-full">
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditingTemplate(null);
            form.reset();
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#5E64A7] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200">
              <Plus className="w-4 h-4 mr-2" />
              {t("templates.add_template", "Add Template")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#F1F6F5] border-0">
            <DialogHeader>
              <DialogTitle className="text-[#34375B]">
                {editingTemplate ? t("templates.edit_email_template", "Edit Email Template") : t("templates.create_email_template", "Create Email Template")}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#8791F3]">{t("templates.template_name", "Template Name")}</FormLabel>
                      <FormControl>
                        <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("templates.enter_template_name", "Enter template name")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="template_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#8791F3]">{t("templates.template_type", "Template Type")}</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md">
                            <SelectValue placeholder={t("templates.select_template_type", "Select template type")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
                          <SelectItem value="initial">{t("templates.template_initial_email", "Initial Email")}</SelectItem>
                          <SelectItem value="reminder_1">{t("templates.template_first_reminder", "First Reminder")}</SelectItem>
                          <SelectItem value="reminder_2">{t("templates.template_second_reminder", "Second Reminder")}</SelectItem>
                          <SelectItem value="reminder_4">{t("templates.template_final_reminder", "Final Reminder")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel className="text-[#8791F3]">{t("templates.template_content", "Template Content")}</FormLabel>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="flex items-center cursor-pointer text-[#34375B]"
                          >
                            <HelpCircle className="h-4 w-4 text-[#34375B]" />
                            <span className="text-sm text-[#34375B] underline">{t("templates.dynamic_values")}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="w-90 p-3">
                          <p className="font-bold text-sm mb-2">{t("templates.available_dynamic_values")}</p>
                          <ul className="space-y-1">
                            <li><code>{"{customer_name}"}</code> - {t("templates.recipient_name")}</li>
                            <li><code>{"{invoice_number}"}</code> - {t("templates.invoice_id")}</li>
                            <li><code>{"{amount}"}</code> - {t("templates.total_invoice_amount")}</li>
                            <li><code>{"{due_date}"}</code> - {t("templates.payment_due_date")}</li>
                            <li><code>{"{business_name}"}</code> - {t("templates.your_business_name")}</li>
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <FormControl>
                    <ReactQuill
                      value={form.getValues("content") || ""}
                      onChange={(value) => form.setValue("content", value)}
                      className="email-template-editor w-full min-h-[200px] bg-white border border-gray-300 rounded-md"
                      theme="snow"
                      modules={{ toolbar: QUILL_TOOLBAR_OPTIONS }}
                      style={{
                        height: "200px",
                        overflowY: "auto",
                      }}
                    />
                  </FormControl>
                  <div className="mt-2 text-xs text-[#636782]">
                    {t("templates.editor_styling_note")}
                  </div>
                  <FormMessage />
                </FormItem>

                <div className="flex justify-end">
                  <Button className="bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200" type="submit" disabled={isLoading}>
                    {isLoading ? (editingTemplate ? t("templates.updating", "Updating...") : t("templates.creating", "Creating...")) : (editingTemplate ? t("templates.update_template", "Update Template") : t("templates.create_template", "Create Template"))}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
